import React, { useEffect, useMemo } from 'react'
import { Billboard } from '@react-three/drei'
import type { ThreeEvent } from '@react-three/fiber'
import type { Vector3 } from 'three'
import * as THREE from 'three'

const LABEL_FONT_SIZE = 30
const LABEL_PADDING = 10
const LABEL_LINE_HEIGHT = LABEL_FONT_SIZE * 1.2
const LABEL_MAX_WIDTH = 320
const LABEL_MAX_LINES = 3
const LABEL_RESOLUTION_SCALE = 2.5

const normalizeLabelText = (text: string) =>
  text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n')

const truncateLine = (
  context: CanvasRenderingContext2D,
  line: string,
  maxWidth: number,
  suffix = '...',
) => {
  if (context.measureText(`${line}${suffix}`).width <= maxWidth) {
    return `${line}${suffix}`
  }

  let truncatedLine = line

  while (truncatedLine && context.measureText(`${truncatedLine}${suffix}`).width > maxWidth) {
    truncatedLine = truncatedLine.slice(0, -1).trimEnd()
  }

  return truncatedLine ? `${truncatedLine}${suffix}` : suffix
}

const createLabelLines = (context: CanvasRenderingContext2D, text: string, maxWidth: number) => {
  const wrappedLines = text
    .split('\n')
    .flatMap((paragraph) => {
      const words = paragraph.split(/\s+/).filter(Boolean)

      if (!words.length) {
        return []
      }

      const paragraphLines: string[] = []
      let currentLine = words[0]

      words.slice(1).forEach((word) => {
        const nextLine = `${currentLine} ${word}`

        if (context.measureText(nextLine).width <= maxWidth) {
          currentLine = nextLine
          return
        }

        paragraphLines.push(currentLine)
        currentLine = word
      })

      paragraphLines.push(currentLine)

      return paragraphLines
    })

  if (wrappedLines.length <= LABEL_MAX_LINES) {
    return wrappedLines
  }

  const visibleLines = wrappedLines.slice(0, LABEL_MAX_LINES)
  visibleLines[LABEL_MAX_LINES - 1] = truncateLine(
    context,
    visibleLines[LABEL_MAX_LINES - 1],
    maxWidth,
  )

  return visibleLines
}

type Props = {
  text: string
  position: Vector3 | [number, number, number]
  color: string
  offset?: { x?: number; y?: number; z?: number }
  useDepthTest?: boolean
  onClick?: (event: ThreeEvent<PointerEvent>) => void
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void
  onPointerEnter?: (event: ThreeEvent<PointerEvent>) => void
  onPointerOver?: (event: ThreeEvent<PointerEvent>) => void
}

export const ElementIdLabel = React.memo(
  ({
    text,
    position,
    offset,
    useDepthTest = true,
    onClick,
    onPointerDown,
    onPointerEnter,
    onPointerOver,
  }: Props) => {
    const pos = Array.isArray(position) ? new THREE.Vector3(...position) : position

    const offsetPosition = useMemo(() => {
      const absX = Math.abs(pos.x)
      const absZ = Math.abs(pos.z)

      let xOffset = 0
      let zOffset = 0

      if (absX > absZ) {
        xOffset = pos.x > 0 ? 0.8 : -0.8
      } else {
        zOffset = pos.z >= 0 ? 0.8 : -0.8
      }

      const defaultOffset = { x: xOffset, y: 0.25, z: zOffset }
      const finalOffset = { ...defaultOffset, ...offset }
      return new THREE.Vector3(pos.x + finalOffset.x, pos.y + finalOffset.y, pos.z + finalOffset.z)
    }, [pos, offset])

    const labelTexture = useMemo(() => {
      const normalizedText = normalizeLabelText(text)

      if (!normalizedText) {
        return null
      }

      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (!context) return null

      context.font = `600 ${LABEL_FONT_SIZE}px sans-serif`

      const lines = createLabelLines(context, normalizedText, LABEL_MAX_WIDTH)

      if (!lines.length) {
        return null
      }

      const textWidth = Math.max(...lines.map((line) => context.measureText(line).width))
      const bgWidth = textWidth + LABEL_PADDING * 2
      const bgHeight = LABEL_LINE_HEIGHT * lines.length + LABEL_PADDING * 2
      const logicalWidth = bgWidth
      const logicalHeight = bgHeight
      const devicePixelRatio =
        typeof window === 'undefined' ? 1 : Math.max(window.devicePixelRatio || 1, 1)
      const resolutionScale = Math.min(devicePixelRatio, LABEL_RESOLUTION_SCALE)

      canvas.width = Math.ceil(logicalWidth * resolutionScale)
      canvas.height = Math.ceil(logicalHeight * resolutionScale)

      context.scale(resolutionScale, resolutionScale)
      context.imageSmoothingEnabled = true

      context.fillStyle = '#111111'
      context.font = `600 ${LABEL_FONT_SIZE}px sans-serif`
      context.textAlign = 'center'
      context.textBaseline = 'middle'
      context.lineJoin = 'round'
      context.miterLimit = 2
      context.lineWidth = 4
      context.strokeStyle = 'rgba(255, 255, 255, 0.92)'

      const startY = LABEL_PADDING + LABEL_LINE_HEIGHT / 2
      lines.forEach((line, index) => {
        const y = startY + LABEL_LINE_HEIGHT * index
        context.strokeText(line, logicalWidth / 2, y)
        context.fillText(line, logicalWidth / 2, y)
      })

      const texture = new THREE.CanvasTexture(canvas)
      texture.colorSpace = THREE.SRGBColorSpace
      texture.generateMipmaps = false
      texture.minFilter = THREE.LinearFilter
      texture.magFilter = THREE.LinearFilter
      texture.needsUpdate = true
      return {
        texture,
        width: logicalWidth,
        height: logicalHeight,
        lineCount: lines.length,
      }
    }, [text])

    useEffect(() => {
      return () => {
        labelTexture?.texture.dispose()
      }
    }, [labelTexture])

    const spriteSize = useMemo(() => {
      if (!labelTexture) return new THREE.Vector3(1, 1, 1)
      const aspect = labelTexture.width / labelTexture.height
      const height = Math.min(0.7, 0.38 + (labelTexture.lineCount - 1) * 0.12)
      return new THREE.Vector3(height * aspect, height, 1)
    }, [labelTexture])

    const handleClick = (event: ThreeEvent<PointerEvent>) => {
      if (!onClick) {
        return
      }

      event.stopPropagation()
      onClick(event)
    }

    const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
      onPointerDown?.(event)
    }

    const handlePointerEnter = (event: ThreeEvent<PointerEvent>) => {
      onPointerEnter?.(event)
    }

    const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
      onPointerOver?.(event)
    }

    if (!labelTexture) return null

    return (
      <Billboard position={[offsetPosition.x, offsetPosition.y, offsetPosition.z]}>
        <sprite
          scale={spriteSize}
          renderOrder={1000}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerEnter={handlePointerEnter}
          onPointerOver={handlePointerOver}>
          <spriteMaterial
            map={labelTexture.texture}
            transparent
            depthTest={useDepthTest}
            depthWrite={false}
          />
        </sprite>
      </Billboard>
    )
  },
)
